/**
 * Mixin for Alpine CSP components that need theme controls.
 * Uses Object.getOwnPropertyDescriptors to preserve getter definitions
 * (plain spread evaluates getters at copy time, before $store exists).
 */
const _themeAwareDescriptors = Object.getOwnPropertyDescriptors({
  toggleTheme() {
    this.$store.theme.toggleTheme();
  },
  get isDark() {
    return this.$store.theme.dark;
  },
  get isLight() {
    return !this.$store.theme.dark;
  },
  get themeLabel() {
    return this.$store.theme.dark ? 'Light mode' : 'Dark mode';
  },
});

export function withThemeAware(component) {
  return Object.defineProperties(component, _themeAwareDescriptors);
}

export default function themeToggle() {
  const applyDocumentTheme = (isDark) => {
    document.documentElement.classList.toggle('dark', isDark);
    document.documentElement.classList.toggle('light', !isDark);
  };

  const safeGetTheme = () => {
    try {
      return localStorage.getItem('theme');
    } catch {
      return null;
    }
  };

  const safeSetTheme = (value) => {
    try {
      localStorage.setItem('theme', value);
    } catch {
      // Ignore storage exceptions (for example, Safari private browsing).
    }
  };

  return {
    dark: false,
    initializeTheme() {
      const storedTheme = safeGetTheme();
      const prefersDark = window.matchMedia
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
        : false;
      const isDark = storedTheme ? storedTheme === 'dark' : prefersDark;
      this.setTheme(isDark, false);
    },
    setTheme(isDark, persist = true) {
      this.dark = isDark;
      applyDocumentTheme(this.dark);
      if (persist) {
        safeSetTheme(this.dark ? 'dark' : 'light');
      }
    },
    toggleTheme() {
      this.setTheme(!this.dark);
    },
    syncFromDocument() {
      this.dark = document.documentElement.classList.contains('dark');
    },
  };
}
